import {EventEmitter, Injectable} from '@angular/core';
import {WRTCProviderService} from '@app/modules/wrtc/services/providers/wrtc-provider';
import {AccessType, ConnectionStatus, LogLevel, LogResource} from '@app/modules/shared/data/constants';
import {Observable, of} from 'rxjs';
import {RoomConnection} from '@app/modules/wrtc/models/room-connection';
import {concatMap, map} from 'rxjs/operators';
import {RemoteParticipantConnectionEventPayload, TrackReceivedEventPayload} from '@app/models/types';
import {Store} from '@ngxs/store';
import {LogService} from '@app/services/log.service';
import {LocalUser, LocalUserAction, LocalUserState} from '@app/state/local-user.state';
import {GameModeAction, GameModeModel, GameModeState} from '@app/state/game-mode.state';
import {ClassroomAction, ClassroomState, ClassroomStateModel} from '@app/state/classroom-state.service';
import {AssignmentService} from '@app/modules/whiteboard/services/assignment.service';
import {MessageHandler} from '@app/services/message-handler';
import {
  ClassroomMessage,
  DataService,
  MessageRecord,
  MsgRecordType,
  RecordArgs,
  RecordStack,
} from '@app/services/data.service';
import {HttpClient} from '@angular/common/http';
import {
  RemoteUserAction,
  RemoteUserAdditionalInfoStateModel,
  RemoteUsersInfoState
} from '@app/state/remote-users.state';
import {WRTCCommands, WRTCEvents, WRTCStates} from '@app/models/commands/wrtc-commands';
import {ModuleService} from '@app/services/module-service';
import {CoreService} from '@app/services/core.service';
import {ChatService} from '@app/modules/chat/services/chat.service';
import * as TwilioDiagnostics from '@twilio/rtc-diagnostics';
import {DiagnosticError, MediaConnectionBitrateTest} from '@twilio/rtc-diagnostics';
import {PrivateWhiteboardService} from '@app/modules/whiteboard/services/private-whiteboard.service';
import {WhiteboardService} from '@app/modules/whiteboard/services/whiteboard.service';
import {PrivateWhiteboard, PrivateWhiteboardState} from '@app/state/private-whiteboard.state';
import {Whiteboard, WhiteboardState} from '@app/state/whiteboard.state';
import {AssignmentState, AssignmentStateModel} from "@app/state/assignment.state";

@Injectable({
  providedIn: 'root'
})
export class WrtcService implements MessageHandler, ModuleService {
  private dataService: DataService;
  private roomConnection: RoomConnection;
  private currentDominantSpeakerId: number;
  private droppedDominantSpeakerTimeoutRefById = {};
  private assignment: AssignmentStateModel;
  private classroom: ClassroomStateModel;
  private whiteboard: Whiteboard;
  private privateWhiteboard: PrivateWhiteboard;
  private gameMode: GameModeModel;
  private localUser: LocalUser;
  private audioFullScreen = new Audio();
  private remoteUsers: { [key: number]: RemoteUserAdditionalInfoStateModel } = {};
  private roomInit: {token: string; roomName: string};
  private localUserSubscriptionStateByRemoteParticipantId: { [key: number]: boolean } = {};
  private coreService: CoreService;
  public handleRecordStack: (recordStack: RecordStack) => Promise<void>; /* should be set in WrtcCommands */
  public privateTalkEndEventEmitter: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private wrtcProviderService: WRTCProviderService,
    private assignmentService: AssignmentService,
    private store: Store,
    private whiteboardService: WhiteboardService,
    private privateWhiteboardService: PrivateWhiteboardService,
    private http: HttpClient,
    private chatService: ChatService
  ) {
    this.listenStoreChanges();
  }

  public setDataService(dataService: DataService): void {
    this.dataService = dataService;
  }

  public listenStoreChanges() {
    this.store.select(LocalUserState.getLocalUser).subscribe((localUser: LocalUser) => {
      this.localUser = localUser;
    });

    this.store.select(RemoteUsersInfoState.getUsersInfo).subscribe(usersInfoById => {
      this.remoteUsers = usersInfoById;
    });

    this.store.select(ClassroomState).subscribe((roomState: ClassroomStateModel) => {
      this.classroom = roomState;
    });

    this.store.select(AssignmentState).subscribe((assignment: AssignmentStateModel) => {
      this.assignment = assignment;
    });

    this.store.select(GameModeState.getGameMode).subscribe((gameModeModel: GameModeModel) => {
      this.gameMode = gameModeModel;
    });

    this.store.select(PrivateWhiteboardState).subscribe((whiteboard: PrivateWhiteboard) => {
      this.privateWhiteboard = whiteboard;
    });

    this.store.select(WhiteboardState).subscribe((whiteboard: Whiteboard) => {
      this.whiteboard = whiteboard;
    });
  }

  public init(token: string, roomName: string) {
    this.registerWrtcProviderEventsListener();

    if (!this.classroom.isClassroomMuted && this.localUser.lateTimeInMinutes > 10) {
      this.sendAudioMute(this.localUser.id).subscribe();
    }

    if (this.localUser.accessType === AccessType.STUDENT && this.localUser.id === 10580) {
      this.wrtcProviderService.setCurrentUserVideoFrameRate(6);
      this.wrtcProviderService.setCurrentUserVideoResolution('176×144');
    }

    this.connectToRoom(token, roomName, this.localUser.id, this.localUser.accessType).subscribe(() => {
      this.store.dispatch(new ClassroomAction.ToggleConnectedToWRTCRoom(true));

      if (this.localUser.accessType === AccessType.SUPPORT) {
        this.wrtcProviderService.toggleDisableSelfAudio(true);
        this.store.dispatch(new LocalUserAction.SetIsAudioMuteState(true));
      }

      this.wrtcProviderService.onUserSpeak(this.localUser.id).subscribe((state: 'start' | 'end'): void => {
        switch (state) {
          case 'start':
            this.coreService.sendStartSpeaking(this.localUser.id).subscribe();
            break;
          case 'end':
            this.coreService.sendEndSpeaking(this.localUser.id).subscribe();
            break;
        }
      });
    }, error => {
      LogService.writeLog(`Can not connect to WRTC Room ${error}`, LogLevel.ERROR, LogResource.WRTC);
      this.store.dispatch(new ClassroomAction.ToggleConnectedToWRTCRoom(false));
    });

    this.getState().subscribe();
  }

  public getState(): Observable<any> {
    Object.keys(this.remoteUsers).forEach((idStr: string) => {
      const id: number = parseInt(idStr);
      this.store.dispatch(new RemoteUserAction.SetUserAudioMute(id, false));
      this.store.dispatch(new RemoteUserAction.SetUserVideoMute(id, false));
    });

    this.store.dispatch(new LocalUserAction.SetIsAudioMuteState(false));
    this.wrtcProviderService.toggleDisableSelfAudio(false);

    this.store.dispatch(new LocalUserAction.SetIsVideoMuteState(false));
    this.wrtcProviderService.toggleDisableSelfVideo(false);
    this.store.dispatch(new ClassroomAction.SetClassroomMuteState(false));
    this.wrtcProviderService.toggleDisableSelfAudio(false);
    this.store.dispatch(new ClassroomAction.EndPrivateTalk());

    return this.http.get(`${this.classroom.vcrURL}/wrtc/get-state`)
      .pipe(map((result: any) => {
        console.log(`GET STATE - ${result.message}`);
        this.handleRecordStack(result.data as RecordStack).then();
      }));
  }

  private onConnected(roomConnection: RoomConnection): void {
    LogService.writeLog(`Connected to WRTC room.`, LogLevel.INFO, LogResource.WRTC);
    this.store.dispatch(new LocalUserAction.SetIsAudioProvided(roomConnection.isAudioProvided));
    this.store.dispatch(new LocalUserAction.SetIsVideoProvided(roomConnection.isVideoProvided));
    if (!roomConnection.isAudioProvided) {
      this.sendMicAccessDenied().subscribe();
      this.chatService.sendSystemInfoMessage(this.localUser,'DENIED ACCESS to microphone');

    } else {
      this.sendMicAccessGranted().subscribe();
    }

    if (!roomConnection.isVideoProvided) {
      this.sendWebCamAccessDenied().subscribe();
      this.chatService.sendSystemInfoMessage(this.localUser,'DENIED ACCESS to webcam');

    } else {
      this.sendWebCamAccessGranted().subscribe();
    }

    if (this.localUser.audio.isMuted) {
      this.wrtcProviderService.toggleDisableSelfAudio(true);
      this.store.dispatch(new LocalUserAction.SetIsAudioMuteState(true));
    } else if (this.classroom.isClassroomMuted && this.localUser.accessType === AccessType.STUDENT) {
      this.sendAudioMute(this.localUser.id);
    }
    // TODO - maybe need to unmute student in else block
    if (this.localUser.video.isMuted) {
      this.wrtcProviderService.toggleDisableSelfVideo(true);
    }

    if (!roomConnection.isAudioProvided && !roomConnection.isVideoProvided) {
      this.store.dispatch(new ClassroomAction.ToggleRoomMutedBecauseOfBlockedMicAnCam(true));
    }
  }

  public connectToRoom(token: string, roomName: string, userId: number, accessType: AccessType): Observable<RoomConnection> {
    this.roomInit = {token, roomName};
    return this.wrtcProviderService
      .connectToRoom(token, userId, roomName, accessType)
      .pipe(concatMap(roomConnection => {
        this.roomConnection = roomConnection;
        this.onConnected(roomConnection);
        return of(roomConnection);
      }));
  }

  public disconnectFromRoom(): void {
    this.wrtcProviderService.disconnectFromRoom();
  }

  //Whenever a participant looses connection with a room and gains it again, we reconnect him with this logic
  public reconnectToRoom(): Observable<RoomConnection> {
    this.store.dispatch(new ClassroomAction.ToggleConnectedToWRTCRoom(false));
    this.store.dispatch(new LocalUserAction.SetIsAudioProvided(false));
    this.store.dispatch(new ClassroomAction.ToggleVideoSectionOpen(false));
    this.store.dispatch(new ClassroomAction.ToggleVideoSectionOpen(true));

    return this.wrtcProviderService
      .reconnectToRoom()
      .pipe(map((roomConnection: RoomConnection) => {
        this.store.dispatch(new ClassroomAction.ToggleConnectedToWRTCRoom(true));
        this.roomConnection = roomConnection;
        this.onConnected(roomConnection);
        return roomConnection;
      }));
  }

  public get isConnected(): boolean {
    return this.wrtcProviderService.isConnected;
  }

  public recoverMuteState(): void {
    // eslint-disable-next-line guard-for-in
    for (const id in this.classroom.privateTalkInfo.participants) {
      const wasMuted: boolean = this.classroom.privateTalkInfo.participants[id].wasMuted;
      if (wasMuted) {
        this.sendAudioMute(parseInt(id)).subscribe();
      } else {
        this.sendAudioUnmute(parseInt(id)).subscribe();
      }
    }
  }

  public recoverStudentMuteState(id: number): void {
    // eslint-disable-next-line guard-for-in
    const wasMuted: boolean = this.classroom.privateTalkInfo.participants[id].wasMuted;
    if (wasMuted) {
      this.sendAudioMute(id).subscribe();
    } else {
      this.sendAudioUnmute(id).subscribe();
    }
  }

  public registerWrtcProviderEventsListener() {
    this.wrtcProviderService.getParticipantConnectedEventEmitter().subscribe((eventInfo: RemoteParticipantConnectionEventPayload) => {
      this.remoteParticipantConnectionStateChangedHandler(eventInfo);
    });
    this.wrtcProviderService.getDominantSpeakerChangedEventEmitter().subscribe((eventInfo: number) => {
      this.dominantSpeakerChangeHandler(eventInfo);
    });
    this.wrtcProviderService.getAudioTrackReceivedEventEmitter().subscribe((eventInfo: TrackReceivedEventPayload) => {
      this.audioTrackReceivedHandler(eventInfo);
    });
    this.wrtcProviderService.getVideoTrackReceivedEventEmitter().subscribe((eventInfo: TrackReceivedEventPayload) => {
      this.videoTrackReceivedHandler(eventInfo);
    });
  }

  public remoteParticipantConnectionStateChangedHandler(eventInfo: RemoteParticipantConnectionEventPayload) {
    const areAllStudentsSwitchedToWbBeforeStudentConnection = this.assignmentService.areAllStudentsSwitchToWhiteBoard() && this.assignment.allowedSwitchingUsers !== null;
    const userId = eventInfo.userId;
    if (eventInfo.isConnected) {
      this.store.dispatch(new RemoteUserAction.SetUserConnectionStatus(userId, ConnectionStatus.ONLINE));

      this.localUserSubscriptionStateByRemoteParticipantId[userId] = this.checkIfLocalUserShouldSubscribeToUserAudio(userId);
    } else {
      const remoteParticipant: RemoteUserAdditionalInfoStateModel = this.remoteUsers[userId];
      if (remoteParticipant) {
        if (remoteParticipant.accessType === AccessType.STUDENT && remoteParticipant.roomConnectionStatus !== ConnectionStatus.OFFLINE) {
          this.store.dispatch(new RemoteUserAction.SetUserConnectionStatus(userId, ConnectionStatus.PENDING));
        } else {
          this.store.dispatch(new RemoteUserAction.SetUserConnectionStatus(userId, ConnectionStatus.OFFLINE));
        }
        this.store.dispatch(new RemoteUserAction.SetUserAudioProvide(userId, false));
        this.store.dispatch(new RemoteUserAction.SetUserVideoProvide(userId, false));
      }
      // TODO - is this need
      if (this.localUserSubscriptionStateByRemoteParticipantId[userId]) {
        delete this.localUserSubscriptionStateByRemoteParticipantId[userId];
      }
    }
    let isAtLeastOneSupportInClassroom = false;
    let isAtLeastOneObserverInClassroom = false;

    Object.keys(this.remoteUsers).forEach((id: string) => {
      const remoteUser = this.remoteUsers[id];

      if (remoteUser.roomConnectionStatus === ConnectionStatus.ONLINE) {
        if (remoteUser.accessType === AccessType.SUPPORT) {
          isAtLeastOneSupportInClassroom = true;
        }
        if (remoteUser.accessType === AccessType.OBSERVER) {
          isAtLeastOneObserverInClassroom = true;
        }
      }
    });
    this.store.dispatch(new ClassroomAction.SetAtLeastOneObserverInClassroom(isAtLeastOneObserverInClassroom));
    this.store.dispatch(new ClassroomAction.SetAtLeastOneSupportInClassroom(isAtLeastOneSupportInClassroom));

    const areAllStudentsSwitchedToWbAfterStudentConnection = this.assignmentService.areAllStudentsSwitchToWhiteBoard() && this.assignment.allowedSwitchingUsers !== null;

    // *NOTE Here we compare results <are all students in wb> BEFORE user connect/disconnect action
    // with <are all students in wb> AFTER student connect/disconnect action to find out do we have
    // to show message to teacher for unload assignment.
    if (areAllStudentsSwitchedToWbBeforeStudentConnection !== areAllStudentsSwitchedToWbAfterStudentConnection) {
      this.assignmentService.toggleShowMessageForUnloadAssignmentIfThereIsNeed();
    }
  }

  public audioTrackReceivedHandler(eventInfo: TrackReceivedEventPayload) {
    if (!this.remoteUsers[eventInfo.userId]) {
      LogService.writeLog(`Can not subscribe to audio participant does not exist ${eventInfo.userId}`, LogLevel.ERROR, LogResource.WRTC);
      return;
    }

    this.wrtcProviderService.setParticipantVoiceLevel(eventInfo.userId, this.remoteUsers[eventInfo.userId].audio.voiceLevel);
    this.store.dispatch(new RemoteUserAction.SetUserAudioProvide(eventInfo.userId, !eventInfo.isMuted));
    this.wrtcProviderService.setSubscribeToAudioTrack(eventInfo.userId, this.localUserSubscriptionStateByRemoteParticipantId[eventInfo.userId]);
  }

  public videoTrackReceivedHandler(eventInfo: TrackReceivedEventPayload) {
    if (!this.remoteUsers[eventInfo.userId]) {
      return;
    }

    this.store.dispatch(new RemoteUserAction.SetUserVideoProvide(eventInfo.userId, true));
    this.wrtcProviderService.toggleSubscribeToVideoTrack(eventInfo.userId, true);
  }

  public getMutedStudentsList(): Observable<any> {
    return this.http.get(`${this.classroom.vcrURL}/wrtc/get-muted-students-list`);
  }

  public sendWakeUpSignal(studentId: number): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WRTCEvents.WAKEUP_SIGNAL,
        new RecordArgs('', studentId)
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/tp`, message);
  }
  public onWakeUpSignal(record: MessageRecord) {
    if (record.targetId === this.localUser.id) {
      this.playFullScreenAudio();
    }
  }

  public playFullScreenAudio(firstEnter: boolean = false): void {
    this.audioFullScreen.src = 'assets/audio/attention.mp3';
    if (firstEnter) {
      this.audioFullScreen.muted = true;
      this.audioFullScreen.play().then(() => {
        this.audioFullScreen.pause();
        this.audioFullScreen.muted = false;
      });
    } else {
      this.audioFullScreen.play().then();
    }
  }

  public sendSelfAudioMute(): Observable<any> {
    const recordData: string = JSON.stringify([this.localUser.id]);
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WRTCStates.MUTE_AUDIO,
        new RecordArgs(recordData)
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/self-mute`, message);
  }
  public onSelfAudioMute(record: MessageRecord) {
    const count: number = JSON.parse(record.data);
    this.store.dispatch(new LocalUserAction.SetIsSelfMutedCount(count));
  }

  public sendUsersAudioMute(targets: number[]): Observable<any> {
    const recordData: string = JSON.stringify(targets);
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WRTCStates.MUTE_AUDIO,
        new RecordArgs(recordData)
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/add`, message);
  }
  public sendAudioMute(targetId: number): Observable<any> {
    return this.sendUsersAudioMute([targetId]);
  }
  public onAudioMute(record: MessageRecord) {
    const ids: number[] = JSON.parse(record.data);
    ids.forEach((id: number) => {
      if (this.localUser.id === id) {
        this.wrtcProviderService.toggleDisableSelfAudio(true);
        this.store.dispatch(new LocalUserAction.SetIsAudioMuteState(true));
      } else {
        this.store.dispatch(new RemoteUserAction.SetUserAudioMute(id, true));
      }
    });
  }

  public sendUsersAudioUnmute(targets: number[]): Observable<any> {
    const recordData: string = JSON.stringify(targets);
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      selfProcess: true,
      mirrorState: WRTCStates.MUTE_AUDIO,
      record: this.buildRecord(
        WRTCStates.UNMUTE_AUDIO,
        new RecordArgs(recordData),
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/sub`, message);
  }
  public sendAudioUnmute(targetId: number): Observable<any> {
    return this.sendUsersAudioUnmute([targetId]);
  }
  public onAudioUnmute(record: MessageRecord) {
    const ids: any = JSON.parse(record.data);
    ids.forEach((id: number) => {
      if (this.localUser.id === id) {
        this.wrtcProviderService.toggleDisableSelfAudio(false);
        this.store.dispatch(new LocalUserAction.SetIsAudioMuteState(false));
      } else {
        this.store.dispatch(new RemoteUserAction.SetUserAudioMute(id, false));
      }
    });
  }

  public sendVideoMute(targetId: number): Observable<any> {
    const recordData: string = JSON.stringify([targetId]);
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WRTCStates.MUTE_VIDEO,
        new RecordArgs(recordData)
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/add`, message);
  }
  public onVideoMute(record: MessageRecord) {
    const ids: any = JSON.parse(record.data);
    ids.forEach((id: number) => {
      if (this.localUser.id === id) {
        this.wrtcProviderService.toggleDisableSelfVideo(true);
        this.store.dispatch(new LocalUserAction.SetIsVideoMuteState(true));
      } else {
        this.store.dispatch(new RemoteUserAction.SetUserVideoMute(id, true));
      }
    });
  }

  public sendVideoUnmute(targetId: number): Observable<any> {
    const recordData: string = JSON.stringify([targetId]);
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      mirrorState: WRTCStates.MUTE_VIDEO,
      record: this.buildRecord(
        WRTCStates.UNMUTE_VIDEO,
        new RecordArgs(recordData)
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/sub`, message);
  }
  public onVideoUnmute(record: MessageRecord) {
    const ids: any = JSON.parse(record.data);
    ids.forEach((id: number) => {
      if (this.localUser.id === id) {
        this.wrtcProviderService.toggleDisableSelfVideo(false);
        this.store.dispatch(new LocalUserAction.SetIsVideoMuteState(false));
      } else {
        this.store.dispatch(new RemoteUserAction.SetUserVideoMute(id, false));
      }
    });
  }

  public sendMuteRoom(mute: boolean, processToAllStudents: boolean = true): Observable<any> {
    const recordData: string = JSON.stringify({mute, processToAllStudents});
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WRTCStates.ROOM_AUDIO,
        new RecordArgs(recordData)
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/t`, message);
  }
  public onMuteRoom(record: MessageRecord) {
    const recordData: {mute: boolean; processToAllStudents: boolean} = JSON.parse(record.data);
    this.store.dispatch(new ClassroomAction.SetClassroomMuteState(recordData.mute));

    if (recordData.processToAllStudents) {
      if (this.localUser.accessType === AccessType.STUDENT) {
        this.wrtcProviderService.toggleDisableSelfAudio(recordData.mute);
        this.store.dispatch(new LocalUserAction.SetIsAudioMuteState(recordData.mute));
      }
      this.store.dispatch(new RemoteUserAction.SetUsersAudioMute(recordData.mute));
    }
  }

  public sendPrivateWhiteboardState(isOn: boolean): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WRTCStates.PRIVATE_WHITEBOARD,
        new RecordArgs(isOn + '')
      )
    };
    return this.http.post(`${this.classroom.vcrURL}/wrtc/t`, message);
  }
  public onPrivateWhiteboardState(record: MessageRecord): void {
    const start: boolean = record.data === 'true';
    if (start) {
      const show: boolean = this.localUser.accessType !== AccessType.STUDENT ||
                            (this.classroom.privateTalkInfo.participants[this.localUser.id] &&
                            this.localUser.accessType === AccessType.STUDENT);

      this.store.dispatch(new ClassroomAction.StartPrivateWhiteboard(show));
    } else {
      this.store.dispatch(new ClassroomAction.EndPrivateWhiteboard());
    }
  }

  public sendPrivateTalk(targetId: number): Observable<any> {
    const recordValue: string = JSON.stringify([targetId]);
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WRTCStates.PRIVATE_TALK,
        new RecordArgs(recordValue)
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/add`, message);
  }
  public onPrivateTalk(record: MessageRecord): void {
    const ids: number[] = JSON.parse(record.data);

    ids.forEach((id: number) => {
      let isMuted: boolean;
      // unmute student if muted
      if (id === this.localUser.id) {
        isMuted = this.localUser.audio.isMuted;
       if (isMuted) {
         this.wrtcProviderService.toggleDisableSelfAudio(false);
         this.store.dispatch(new LocalUserAction.SetIsAudioMuteState(false));
       }
      } else {
        const remoteUserAdditionInfo: RemoteUserAdditionalInfoStateModel = this.remoteUsers[id];
        isMuted = remoteUserAdditionInfo.audio.isMuted;
        if (isMuted) {
          this.store.dispatch(new RemoteUserAction.SetUserAudioMute(remoteUserAdditionInfo.id, false));
        }
      }
       //  Prevent duplicate insert user id to the list. In case of selfProcess user already add participant id before sending command.
      if (!this.classroom.privateTalkInfo.participants[id]) {
        this.store.dispatch(new ClassroomAction.AddPrivateTalkParticipant(id, isMuted));
      }
    });

    if (this.localUser.id === record.ownerId) {
      this.wrtcProviderService.toggleDisableSelfAudio(false);
      this.store.dispatch(new LocalUserAction.SetIsAudioMuteState(false));
    }

    this.updateParticipantsSubscriptionRules();

    if (this.classroom.privateTalkInfo.isPrivateWhiteboardOn &&
      this.localUser.accessType === AccessType.STUDENT &&
      this.classroom.privateTalkInfo.participants[this.localUser.id]) {
      this.store.dispatch(new ClassroomAction.ShowPrivateWhiteboard(true));
    }
  }

  public sendEndPrivateTalk(): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      selfProcess: true,
      mirrorState: WRTCStates.PRIVATE_TALK,
      record: this.buildRecord(
        WRTCStates.END_PRIVATE_TALK,
        new RecordArgs('')
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/del`, message);
  }
  public onEndPrivateTalk(record: MessageRecord) {
    this.privateTalkEndEventEmitter.emit();
    this.store.dispatch(new ClassroomAction.EndPrivateTalk());
    this.getMutedStudentsList().subscribe((mutedStudentIds: number[]) => {
      mutedStudentIds.forEach(userId => {
        this.muteAudio(userId, true);
      });
      if (this.classroom.isClassroomMuted) {
        if (this.localUser.accessType === AccessType.STUDENT && !this.localUser.audio.isMuted) {
          this.wrtcProviderService.toggleDisableSelfAudio(true);
        }
        for (const id in this.remoteUsers) {
          if (this.remoteUsers[id] && this.remoteUsers[id].accessType === AccessType.STUDENT && !this.remoteUsers[id].audio.isMuted) {
            this.store.dispatch(new RemoteUserAction.SetUserAudioMute(parseInt(id), true));
          }
        }
      }
    });
   this.updateParticipantsSubscriptionRules();
  }

  public sendExcludeFromPrivateTalk(targetId: number): Observable<any> {
    const recordValue: string = JSON.stringify([targetId]);
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      selfProcess: true,
      mirrorState: WRTCStates.PRIVATE_TALK,
      record: this.buildRecord(
        WRTCStates.EXCLUDE_FROM_PRIVATE_TALK,
        new RecordArgs(recordValue)
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/sub`, message);
  }
  public onExcludeFromPrivateTalk(record: MessageRecord) {
    const ids: number[] = JSON.parse(record.data);
    this.getMutedStudentsList().subscribe((mutedUsersIds: number[]) => {
      ids.forEach((id: number) => {
        this.store.dispatch(new ClassroomAction.RemovePrivateTalkParticipant(id));
        if (
          mutedUsersIds.includes(id) ||
          (this.classroom.isClassroomMuted && this.classroom.privateTalkInfo.participants[id])
        ) {
          this.muteAudio(id, true);
        }
        if (this.localUser.id === id) {
          this.store.dispatch(new ClassroomAction.ShowPrivateWhiteboard(false));
        }

        WhiteboardService.hidePointer(this.privateWhiteboard.tools.studentPointerTools[id]);
      });
      this.updateParticipantsSubscriptionRules();
    });
  }

  public sendTeacherTeam(teamId: number): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WRTCStates.TEACHER_IN_TEAM,
        new RecordArgs(teamId + '')
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/t`, message);
  }
  public onTeacherTeam(record: MessageRecord) {
    const teamId = Number(record.data);
    if (teamId) {
      this.store.dispatch(new GameModeAction.TeacherSwitchToTeam(teamId));
    } else {
      this.store.dispatch(new GameModeAction.TeacherLeaveTeam());
    }
    this.updateParticipantsSubscriptionRules();
  }

  public sendFrameRate(frameRate: number, targetId: number): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WRTCStates.FRAME_RATE,
        new RecordArgs(frameRate + '', targetId)
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/tp`, message);
  }
  public onFrameRate(record: MessageRecord) {
    const frameRate = Number(record.data);
    if (record.targetId === this.localUser.id) {
      this.wrtcProviderService.setCurrentUserVideoFrameRate(frameRate);
      this.reconnectToRoom().subscribe();
    }
  }

  public sendResolution(resolution: number, targetId: number): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WRTCStates.RESOLUTION,
        new RecordArgs(resolution + '', targetId)
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/tp`, message);
  }
  public onResolution(record: MessageRecord): void {
    const resolution: string = record.data;
    if (record.targetId === this.localUser.id) {
      this.wrtcProviderService.setCurrentUserVideoResolution(resolution);
      this.reconnectToRoom().subscribe();
    }
  }

  public sendReload(targetId: number): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WRTCEvents.RELOAD_USER,
        new RecordArgs('', targetId)
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/tp`, message);
  }
  public onReload(record: MessageRecord) {
    if (record.targetId === this.localUser.id) {
      this.reconnectToRoom().subscribe();
    }
  }

  public sendMicAccessDenied(): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WRTCStates.MIC_ACCESS_DENIED,
        new RecordArgs('')
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/b`, message);
  }
  public onMicAccessDenied(record: MessageRecord): void {
  }

  public sendMicAccessGranted(): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WRTCStates.MIC_ACCESS_GRANTED,
        new RecordArgs('')
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/b`, message);
  }
  public onMicAccessGranted(record: MessageRecord): void {}

  public sendWebCamAccessDenied(): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WRTCStates.CAMERA_ACCESS_DENIED,
        new RecordArgs('')
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/b`, message);
  }
  public onWebCamAccessDenied(record: MessageRecord) {
  }

  public sendWebCamAccessGranted(): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WRTCStates.CAMERA_ACCESS_GRANTED,
        new RecordArgs('')
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/b`, message);
  }
  public onWebCamAccessGranted(record: MessageRecord) {}

  public sendVoiceLevel(level: number, targetId: number): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WRTCStates.VOICE_LEVEL,
        new RecordArgs(level + '', targetId)
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/t`, message);
  }
  public onVoiceLevel(record: MessageRecord) {
    const voiceLevel = Number(record.data);
    if (this.localUser.id !== record.targetId) {
      this.store.dispatch(new RemoteUserAction.SetUserVoiceLevel(record.targetId, voiceLevel));
      this.setParticipantVoiceLevel(record.targetId, voiceLevel);
    }
  }

  public sendReset(): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WRTCEvents.RESET,
        new RecordArgs('')
      )
    };
    return this.send(`${this.classroom.vcrURL}/wrtc/purge`, message);
  }
  public onReset(record: MessageRecord) {
    this.store.dispatch(new ClassroomAction.EndPrivateTalk());
    this.store.dispatch(new GameModeAction.EndGameMode());
    this.localUserSubscriptionStateByRemoteParticipantId = {};

    this.store.dispatch(new LocalUserAction.SetIsAudioMuteState(false));
    this.store.dispatch(new LocalUserAction.SetIsAudioProvided(false));
    this.store.dispatch(new LocalUserAction.SetIsVideoMuteState(false));
    this.store.dispatch(new LocalUserAction.SetIsVideoProvided(false));
    this.store.dispatch(new RemoteUserAction.SetUsersAudioMute(false));
    this.store.dispatch(new RemoteUserAction.SetUsersVideoMute(false));
    this.store.dispatch(new RemoteUserAction.SetUsersAudioProvide(false));
    this.store.dispatch(new RemoteUserAction.SetUsersVideoProvide(false));

    this.store.dispatch(new ClassroomAction.SetClassroomMuteState(false));
    this.reconnectToRoom().subscribe();
    this.getState().subscribe();
    this.coreService.afterResetEmitter$.emit();
  }

  public muteAudio(targetId: number, isMute: boolean): void {
    if (this.localUser.id === targetId) {
      this.wrtcProviderService.toggleDisableSelfAudio(isMute);
      this.store.dispatch(new LocalUserAction.SetIsAudioMuteState(isMute));
    } else {
      this.store.dispatch(new RemoteUserAction.SetUserAudioMute(targetId, isMute));
    }
  }

  public setParticipantVoiceLevel(userId: number, voiceLevel: number) {
    this.wrtcProviderService.setParticipantVoiceLevel(userId, voiceLevel);
  }

  public updateParticipantsSubscriptionRules() {
    for (const userId in this.localUserSubscriptionStateByRemoteParticipantId) {
      if (this.localUserSubscriptionStateByRemoteParticipantId[userId] !== undefined) {
        const id = parseInt(userId);
        const doSubscribe = this.checkIfLocalUserShouldSubscribeToUserAudio(id);
        if (doSubscribe !== this.localUserSubscriptionStateByRemoteParticipantId[id]) {
          this.localUserSubscriptionStateByRemoteParticipantId[id] = doSubscribe;
          this.wrtcProviderService.setSubscribeToAudioTrack(id, doSubscribe);
        }
      }
    }
  }

  public checkIfLocalUserShouldSubscribeToUserAudio(id): boolean {
    let isNeedToSubscribe = true;
    if (this.classroom.privateTalkInfo.isStarted && this.gameMode.isTeamTalkOn) {
      isNeedToSubscribe = this.doesCurrentUserShouldSubscribeToUserAudioInGameMode(id);

      const isLocalUserParticipantOfPrivateCall: boolean = this.isUserPrivateCallParticipant(this.localUser.id);
      const isRemoteUserParticipantOfPrivateCall: boolean = this.isUserPrivateCallParticipant(id);

      /* !== is equivalent of xor operator */
      if (isLocalUserParticipantOfPrivateCall !== isRemoteUserParticipantOfPrivateCall) {
        isNeedToSubscribe = false;
      }
    } else if (this.classroom.privateTalkInfo.isStarted) {
      isNeedToSubscribe = this.doseCurrentUserShouldSubscribeToUserAudioInPrivateMode(id);
    } else if (this.gameMode.isStarted) {
      isNeedToSubscribe = this.doesCurrentUserShouldSubscribeToUserAudioInGameMode(id);
    }

    return isNeedToSubscribe;
  }

  public doseCurrentUserShouldSubscribeToUserAudioInPrivateMode(id) {
    const isLocalUserParticipantOfPrivateCall: boolean = this.isUserPrivateCallParticipant(this.localUser.id);

    const isRemoteUserParticipantOfPrivateCall: boolean = this.isUserPrivateCallParticipant(id);

    return isLocalUserParticipantOfPrivateCall && isRemoteUserParticipantOfPrivateCall;
  }

  public doesCurrentUserShouldSubscribeToUserAudioInGameMode(id) {
    let subscribe;
    const remoteUser = this.remoteUsers[id];

    switch (this.localUser.accessType) {
      case AccessType.TEACHER:
      case AccessType.OBSERVER: {
        if (remoteUser.accessType === AccessType.TEACHER || remoteUser.accessType === AccessType.SUPPORT) {
          subscribe = true;
        } else {
          const newAddedParticipantTeamIndex = this.gameMode.participantsTeamIndexById[id];
          // case when teacher can speak to both teams
          if (this.gameMode.teacherOnTeamIndex === null) {
            subscribe = true;
          } else {
            subscribe = this.gameMode.teacherOnTeamIndex === newAddedParticipantTeamIndex;
          }
        }
        break;
      }
      case AccessType.STUDENT: {
        // TODO - need to revisit
        const currentParticipantTeamIndex = this.gameMode.participantsTeamIndexById[this.localUser.id];
        // case when new added participant is teacher /support
        if (remoteUser.accessType === AccessType.TEACHER || remoteUser.accessType === AccessType.SUPPORT) {
          // case when teacher can speak to both teams
          if (this.gameMode.teacherOnTeamIndex === null) {
            subscribe = true;
          } else {
            subscribe = this.gameMode.teacherOnTeamIndex === currentParticipantTeamIndex;
          }
          // case when new added participant is student
        } else {
          const newAddedParticipantTeamIndex = this.gameMode.participantsTeamIndexById[id];
          if (!this.gameMode.isTeamTalkOn) {
            subscribe = true;
          } else {
            subscribe = currentParticipantTeamIndex === newAddedParticipantTeamIndex;
          }
        }
        break;
      }
      case AccessType.SUPPORT: {
        subscribe = true;
        break;
      }
    }
    // console.log("--zz CP: " + self.initData.identity + " to RP: " + identity + " subscribe " + subscribe)
    return subscribe;
  }

  public isUserPrivateCallParticipant(id: number) {
    const user = this.getUserById(id);

    return !!this.classroom.privateTalkInfo.participants[id] ||
      (user.accessType === AccessType.TEACHER || user.accessType === AccessType.SUPPORT || user.accessType === AccessType.OBSERVER);
  }

  public getUserById(id: number) {
    if (this.localUser.id === id) {
      return this.localUser;
    } else {
      return this.remoteUsers[id];
    }
  }

  public dominantSpeakerChangeHandler(userId: number) {
    const newDominantSpeakerId = userId;

    if (this.currentDominantSpeakerId) {
      const currentParticipantId = this.currentDominantSpeakerId;
      this.droppedDominantSpeakerTimeoutRefById[currentParticipantId] = setTimeout(
        () => {
          this.store.dispatch(new RemoteUserAction.SetIsDominantSpeaker(currentParticipantId, false));
          delete this.droppedDominantSpeakerTimeoutRefById[currentParticipantId];
        }, 3000);
    }
    this.currentDominantSpeakerId = null;

    if (newDominantSpeakerId && this.remoteUsers[newDominantSpeakerId] && this.remoteUsers[newDominantSpeakerId].accessType === AccessType.STUDENT) {
      if (this.droppedDominantSpeakerTimeoutRefById[newDominantSpeakerId]) {
        clearTimeout(this.droppedDominantSpeakerTimeoutRefById[newDominantSpeakerId]);
        delete this.droppedDominantSpeakerTimeoutRefById[newDominantSpeakerId];
      }

      this.currentDominantSpeakerId = newDominantSpeakerId;
      this.store.dispatch(new RemoteUserAction.SetIsDominantSpeaker(newDominantSpeakerId, true));
    }

  }

  private send(destination: string, data: object, options?: object): Observable<object> {
    return this.http.post(destination, data, options);
  }

  public buildRecord(command: string, args: RecordArgs): MessageRecord {
    const msgType = WRTCStates[command] ? MsgRecordType.STATE : WRTCEvents[command] ? MsgRecordType.EVENT : null;
    return {
      category: WRTCCommands.category,
      name: command,
      data: args.data,
      type: msgType,
      ownerId: null, /* must be set server-side on receive */
      targetId: args.targetId
    };
  }

  public dropRoomMuteBecauseOfBlockedMicAndCam(): void {
    // eslint-disable-next-line guard-for-in
    for (const userId in this.localUserSubscriptionStateByRemoteParticipantId) {
      const id: number = parseInt(userId);
      if (this.localUserSubscriptionStateByRemoteParticipantId[id]) {
        this.wrtcProviderService.setSubscribeToAudioTrack(id, this.localUserSubscriptionStateByRemoteParticipantId[id]);
      }
    }
  }

  public setCoreService(coreService: CoreService): void {
    this.coreService = coreService;
  }

  public getIceServersUrls(): any {
    return this.http.get(`${this.classroom.vcrURL}/wrtc/ice-servers`);
  }

  public startPrivateWhiteboard(): void {
    this.sendPrivateWhiteboardState(true).subscribe();
  }

  public endPrivateWhiteboard(): void {
    this.privateWhiteboardService.sendPurge();
    this.sendPrivateWhiteboardState(false).subscribe();
  }

  public static testMediaConnectionBitrate(
    iceServers: RTCIceServer[],
    onBitrate: (bitrate: number) => void,
    onError: (error: DiagnosticError) => void,
    onEnd: (report: MediaConnectionBitrateTest.Report) => void
  ): MediaConnectionBitrateTest {
    const mediaConnectionBitrateTest: MediaConnectionBitrateTest = TwilioDiagnostics.testMediaConnectionBitrate({iceServers});
    mediaConnectionBitrateTest.on(MediaConnectionBitrateTest.Events.Bitrate, (bitrate) => {
      onBitrate(bitrate);
    });

    mediaConnectionBitrateTest.on(MediaConnectionBitrateTest.Events.Error, (error) => {
      onError(error);
    });

    mediaConnectionBitrateTest.on(MediaConnectionBitrateTest.Events.End, (report) => {
      onEnd(report);
    });

    return mediaConnectionBitrateTest;
  }
}
